// react
import { useCallback, useEffect, useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import { Badge, Box, Button, Popover, Tooltip } from '@mui/material';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { daDK } from '@mui/x-date-pickers/locales';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// components
import { getTimestampSummaryValidationStatus } from '@/utils/getTimestampSummaryValidationStatus';

// relative
import { DateTime } from 'luxon';

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  const year = date.setLocale('da').toFormat('yyyy');
  return `${day} ${month} ${year}`;
};

function buildYearShortcuts(count = 4) {
  const now = DateTime.now(); // call DateTime.now() only once
  return Array.from({ length: count }, (_, i) => {
    const yearStart = now.minus({ years: i }).startOf('year');
    const yearEnd = yearStart.endOf('year');

    return {
      label: i === 0 ? 'Dette år' : String(yearStart.year),
      getValue: () => [yearStart, i === 0 ? now : yearEnd],
    };
  });
}

const esgShortcutItems = [...buildYearShortcuts(4)];

const shortcutsItems = [
  {
    label: '2026',
    getValue: () => [DateTime.now().startOf('year'), DateTime.now()],
  },
  {
    label: '2025',
    getValue: () => {
      const lastYearStart = DateTime.now().minus({ years: 1 }).startOf('year');
      const lastYearEnd = DateTime.now().minus({ years: 1 }).endOf('year');
      return [lastYearStart, lastYearEnd];
    },
  },
  // { label: 'Sidste uge', getValue: () => [DateTime.now().minus({ days: 7 }).startOf('week'), DateTime.now().minus({ days: 7 }).endOf('week')] },
  {
    label: 'Sidste 7 dage',
    getValue: () => [DateTime.now().minus({ days: 7 }), DateTime.now()],
  },
  {
    label: 'Sidste 4 uger',
    getValue: () => [DateTime.now().minus({ weeks: 4 }), DateTime.now()],
  },
];

const DateRangeSelector = ({
  setTimeRange,
  timeRange,
  setDuration,
  timestampSummary,
  enableEsgShortcuts = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const selectedRange = useMemo(
    () => [
      DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
      DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
    ],
    [timeRange.start, timeRange.end],
  );

  const [tempRange, setTempRange] = useState(selectedRange);

  const { t } = useTranslation(['common', 'energyxplorer']);

  useEffect(() => {
    setDuration(selectedRange[1].diff(selectedRange[0], 'days').days);
  }, [selectedRange, setDuration]);

  const handleRangeChange = (newRange) => {
    if (!newRange || !newRange[0] || !newRange[1]) return;
    setTempRange(newRange);
  };

  const zone = 'Europe/Copenhagen';

  const handleConfirm = useCallback(() => {
    if (!tempRange?.[0] || !tempRange?.[1]) return;

    const now = DateTime.now().setZone(zone);

    // Your logic currently rounds to whole days:
    const roundedStart = tempRange[0].setZone(zone).startOf('day');
    let roundedEnd = tempRange[1].setZone(zone).endOf('day');

    if (roundedEnd > now) roundedEnd = now;

    if (roundedEnd >= roundedStart) {
      const durationDays = roundedEnd.diff(roundedStart, 'days').days;

      setTimeRange({
        start: roundedStart.toUTC().toMillis(),
        end: roundedEnd.toUTC().toMillis(),
      });

      setDuration(durationDays);
    }

    setAnchorEl(null);
  }, [tempRange, setTimeRange, setDuration]);

  const handleClick = (event) => {
    setTempRange(selectedRange); // Reset temp range on opening
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const validation = getTimestampSummaryValidationStatus(
    timeRange,
    timestampSummary,
  );

  const handleAdjustRange = useCallback(() => {
    const [adjustedStart, adjustedEnd] = validation.adjustedRange;

    setTimeRange({
      start: adjustedStart.toUTC().toMillis(),
      end: adjustedEnd.toUTC().toMillis(),
    });

    setDuration(adjustedEnd.diff(adjustedStart, 'days').days);
    setAnchorEl(null);
  }, [validation.adjustedRange, setTimeRange, setDuration]);

  // Check if current time range is outside bounds
  const isOutOfBounds = validation.isOutOfBounds;

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={t('locale')}
      localeText={
        t('locale') === 'da'
          ? daDK.components.MuiLocalizationProvider.defaultProps.localeText
          : null
      }
    >
      <Tooltip
        title={t('common:tooltips.selectPeriod')}
        enterDelay={500}
        placement="top"
      >
        <Button
          startIcon={
            isOutOfBounds ? (
              <Badge
                badgeContent={
                  <WarningRoundedIcon fontSize="small" color="warning" />
                }
                sx={{ mr: 1 }}
              >
                <CalendarTodayRoundedIcon fontSize="small" />
              </Badge>
            ) : (
              <CalendarTodayRoundedIcon />
            )
          }
          variant="text"
          onClick={handleClick}
          disableRipple
          sx={{
            ':hover': {
              background: 'none',
            },
          }}
        >
          {selectedRange[0] && selectedRange[1]
            ? `${formatDate(selectedRange[0])} - ${formatDate(
                selectedRange[1],
              )}`
            : 'Vælg periode'}
        </Button>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ border: '1px solid', borderColor: 'divider' }}>
          <StaticDateRangePicker
            displayStaticWrapperAs="mobile"
            disableFuture
            value={tempRange}
            onChange={handleRangeChange}
            maxDateTime={DateTime.now()}
            views={['year', 'month', 'day', 'hours']}
            format="dd/MM/yyyy HH:mm"
            ampm={false}
            calendars={2}
            dayOfWeekFormatter={(day) =>
              day.toLocaleString({ weekday: 'short' })
            }
            slotProps={{
              shortcuts: {
                items: enableEsgShortcuts ? esgShortcutItems : shortcutsItems,
                style: { borderRadius: 2 },
              },
              actionBar: {
                actions: [], // Other options: 'cancel', 'today'
              },
            }}
          />
          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ p: 2, backgroundColor: 'background.paper' }}
          >
            {validation.isOutOfBounds && !validation.isInverted && (
              <Button
                startIcon={
                  <Badge
                    badgeContent={
                      <WarningRoundedIcon fontSize="small" color="warning" />
                    }
                    sx={{ mr: 1 }}
                  >
                    <CalendarTodayRoundedIcon fontSize="small" />
                  </Badge>
                }
                onClick={handleAdjustRange}
              >
                {t('daterange.adjust_period', { ns: 'energyxplorer' })}
              </Button>
            )}
            <Button
              onClick={handleConfirm}
              variant="contained"
              sx={{ ml: 1 }}
              disabled={!tempRange[0] || !tempRange[1]} // Prevent confirmation if range is incomplete
            >
              {t('buttonLabel.submit')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
};

export default DateRangeSelector;
